import type {
  ApprovalAction,
  OrderType,
  PaperRecoveryInput,
  TenderInput,
  WriteOffKind,
} from '@didto/domain';
import {
  WRITE_OFF_LABEL,
  formatInTimeZone,
  formatMinor,
  isSettled,
  isToSettle,
  paperRecoveryPayload,
  readyForLabel,
  refundableMinor,
  remainingMinor,
  serveOffer,
  utcIsoToWallTime,
  uuidv7,
  validateOrderHeader,
  wallTimeToUtcIso,
} from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  Input,
  PageHeader,
  Sheet,
  Surface,
} from '@didto/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDirectActions } from '@/app/directActions';
import { useSession } from '@/app/session';
import { useShellBar } from '@/app/shellBar';
import { useToast } from '@/app/toast';
import { useSetting } from '@/app/useSetting';
import { errorText } from '@/lib/errors';
import type { AppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import {
  FULFILMENT_LABEL,
  ORDER_TYPE_LABEL,
  fetchOpenOrders,
  fetchOrderDetail,
  hintOf,
  latestOrderVersion,
  orderKeys,
} from '@/lib/orders';
import type { Order, OrderDetail, OrderItem, Ticket } from '@/lib/orders';
import { fetchOrderMoney, fetchPaymentMethods, paymentKeys } from '@/lib/payments';
import type { OrderMoney, Payment, Receipt } from '@/lib/payments';
import { outbox } from '@/lib/outbox';
import { topicPos, useRealtimeInvalidate } from '@/lib/realtime';
import { useShiftState } from '@/lib/shifts';
import { ORDER_STATUS_TONE, PAYMENT_STATUS_TONE, fulfilmentTone, ticketTone } from '@/lib/status';
import { allowanceNote, approvalCopy, approvalRequest, voidApprovalPermission } from './approvals';
import { TerminalPicker, moneyAtPayload, useCashMovesNow } from './moneyAt';
import type { RefundInput } from './paymentSheets';
import { CheckoutSheet, MoneyPanel, RefundSheet, SlipSheet } from './paymentSheets';
import { GiveBackSheet, GiveBacksPanel, reversalIdsOf } from './giveBacks';
import type { WriteOffInput, WriteOffRow } from './writeOffs';
import {
  ReinstateSheet,
  WriteOffSheet,
  WriteOffsPanel,
  useOrderWriteOffs,
  usePickerEmployees,
} from './writeOffs';
import type { OrderReturns, ReturnInput } from './returns';
import { ReturnSheet, ReturnsPanel, returnableOf, useOrderReturns } from './returns';
import type { DuplicateRequest } from './references';
import { DuplicateReferenceSheet, duplicateOf, withDuplicateReason } from './references';
import type { PosProduct, UiLine } from './posData';
import {
  describeLine,
  needsPicker,
  sameChoice,
  toCartLine,
  uiLineTotal,
  usePosCatalog,
  usePosContext,
  useRun,
  useStationHealth,
  useStations,
  useTables,
} from './posData';
import { describeOffline } from './posData';
import { CorrectServeButton, serveCorrection } from '../expo/serveCorrection';
import type { CorrectServe } from '../expo/serveCorrection';
import { ForceClearSheet, TableOccupancyList } from '../tables/TableOccupancy';
import { ShiftDesk } from './shift';
import { useOpenSessions, useTableClear } from '../tables/tableSupport';
import { PaperOrderFields } from './paperOrder';
import type { Approval, ApprovalRequest, HeaderState, ReasonRequest } from './sheets';
import {
  ApprovalSheet,
  EMPTY_HEADER,
  LineBadge,
  LineSheet,
  OrderHeaderFields,
  QtyStepper,
  ReasonSheet,
} from './sheets';
import './pos.css';

/**
 * Cashier POS (Phase 3 and 4). Two modes on one screen:
 *  - cart: a local cart with the order header; PLACE ORDER creates the order and its number.
 *  - order: an open order is loaded; lines can be added, adjusted before send, paid (cash,
 *    GCash, split tenders, deposits on advance orders), sent to production (pay-first gate; the
 *    cashier sends unpaid directly, ADR-022), refunded, cancelled or voided (reversing money
 *    stays with the owner).
 * The server re-validates everything; this page only makes the happy path fast.
 */
interface HeaderPayload {
  type: OrderType;
  dining_table_id: string | null;
  covers: number | null;
  customer_name: string | null;
  scheduled_for: string | null;
  notes: string | null;
}

function headerPayload(
  h: HeaderState,
  timeZone: string,
): { ok: true; value: HeaderPayload } | { ok: false; error: string } {
  const err = validateOrderHeader({
    type: h.type,
    dining_table_id: h.dining_table_id || null,
    scheduled_for: h.scheduled_wall || null,
  });
  if (err) return { ok: false, error: err };
  let scheduled: string | null = null;
  if (h.type === 'advance') {
    scheduled = wallTimeToUtcIso(h.scheduled_wall, timeZone);
    if (!scheduled) return { ok: false, error: 'Enter a valid date and time' };
    if (new Date(scheduled).getTime() <= Date.now())
      return { ok: false, error: 'The scheduled time must be in the future' };
  }
  const covers = h.type === 'dine_in' && h.covers.trim() ? Number(h.covers) : null;
  if (covers !== null && (!Number.isInteger(covers) || covers < 1 || covers > 99))
    return { ok: false, error: 'Guests must be a whole number between 1 and 99' };
  return {
    ok: true,
    value: {
      type: h.type,
      dining_table_id: h.type === 'dine_in' ? h.dining_table_id : null,
      covers,
      customer_name: h.customer_name.trim() || null,
      scheduled_for: scheduled,
      notes: h.notes.trim() || null,
    },
  };
}

/** Lower case and without accents, so "cafe" finds "Café". */
const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
/** The words typed in the product search; a product matches when its name holds every one of them. */
function searchWords(query: string): string[] {
  return fold(query).split(/\s+/).filter(Boolean);
}
function nameMatches(name: string, words: string[]): boolean {
  const folded = fold(name);
  return words.every((w) => folded.includes(w));
}

function headerFromOrder(o: Order, timeZone: string): HeaderState {
  return {
    type: o.type,
    dining_table_id: o.dining_table_id ?? '',
    covers: o.covers ? String(o.covers) : '',
    customer_name: o.customer_name ?? '',
    scheduled_wall: o.scheduled_for ? utcIsoToWallTime(o.scheduled_for, timeZone) : '',
    notes: o.notes ?? '',
  };
}

const money = (o: Order) => ({
  totalMinor: o.total_minor,
  paidMinor: o.paid_minor,
  refundedMinor: o.refunded_minor,
  writtenOffMinor: o.written_off_minor,
});

/**
 * A payment refused after a delayed send stays in the outbox as "payment not recorded". When the same tenders are then
 * recorded (the cashier answered the reference question and took them again), that entry is done: sending it again
 * would only be refused, and discarding it would report money as unrecorded that was recorded (ADR-032).
 */
function dropRecordedFailures(result: Record<string, unknown>) {
  const ids = new Set(
    Array.isArray(result.payment_ids)
      ? result.payment_ids.filter((x): x is string => typeof x === 'string')
      : [],
  );
  if (ids.size === 0) return;
  for (const f of outbox.failed()) {
    if (f.entry.envelope.kind !== 'payment.capture') continue;
    const tenders = (f.entry.envelope.payload as { tenders?: { id?: string }[] }).tenders ?? [];
    if (tenders.some((t) => !!t.id && ids.has(t.id))) outbox.drop(f.entry.envelope.command_id);
  }
}

/** Serve commands go straight to the server (no outbox), like KDS bumps (ADR-020, ADR-021). */
const SERVE_INVALIDATE: readonly (readonly string[])[] = [['orders'], ['expo'], ['table-sessions']];

export function PosPage() {
  const s = useSession();
  const ctx = usePosContext();
  const shellBar = useShellBar();
  const { toast } = useToast();
  // ADR-037: while the server cannot be reached the POS is read-only
  const online = useOnline();
  const run = useRun(ctx);
  const qc = useQueryClient();
  const catalog = usePosCatalog(ctx.locationId);
  const tables = useTables(ctx.locationId);
  const openOrders = useQuery({
    queryKey: orderKeys.open(ctx.locationId),
    enabled: !!ctx.locationId,
    queryFn: () => fetchOpenOrders(ctx.locationId),
    refetchInterval: 15_000,
  });
  const methods = useQuery({
    queryKey: paymentKeys.methods,
    queryFn: fetchPaymentMethods,
    staleTime: 60_000,
  });
  const stations = useStations(ctx.locationId);
  const health = useStationHealth(ctx.locationId, ctx.principal);
  const isDevice = ctx.principal === 'device';
  // ADR-024: the tablet's drawer and shift (never expected cash); money goes through it
  const shiftState = useShiftState(isDevice, ctx.terminal?.id ?? '', ctx.locationId);
  const moneyAt = moneyAtPayload(ctx);
  const cashNow = useCashMovesNow();
  // Phase 5: a production hint (ticket started, ready, cancelled) refetches the order queries early.
  useRealtimeInvalidate(ctx.locationId ? topicPos(ctx.locationId) : null, [
    ['orders'],
    ['table-sessions'],
  ]);

  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  // Phase 6: occupancy is the table session, not the open order (critical refinement 1).
  const sessions = useOpenSessions(ctx.locationId);
  const tableClear = useTableClear(
    ctx.principal === 'human' ? ctx.locationId : null,
    s.can('table.force_clear'),
  );
  const undoWindow = Number(useSetting('serve.undo_window_seconds'));
  const serve = useDirectActions(
    ctx.principal === 'human' ? ctx.locationId : null,
    SERVE_INVALIDATE,
  );
  const detail = useQuery({
    queryKey: orderKeys.detail(activeOrderId ?? ''),
    enabled: !!activeOrderId,
    queryFn: () => fetchOrderDetail(activeOrderId ?? ''),
    refetchInterval: 10_000,
  });
  const orderMoney = useQuery({
    queryKey: paymentKeys.money(activeOrderId ?? ''),
    enabled: !!activeOrderId,
    queryFn: () => fetchOrderMoney(activeOrderId ?? ''),
    refetchInterval: 10_000,
  });

  const [cart, setCart] = useState<UiLine[]>([]);
  const [header, setHeader] = useState<HeaderState>(EMPTY_HEADER);
  // ADR-040: the order on the screen was taken on paper during an outage
  const [paper, setPaper] = useState<PaperRecoveryInput | null>(null);
  // a cart emptied (line by line, or placed) starts the next order afresh: never a live order marked as from paper
  const cartLength = useRef(0);
  useEffect(() => {
    if (cartLength.current > 0 && cart.length === 0) setPaper(null);
    cartLength.current = cart.length;
  }, [cart.length]);
  const [categoryId, setCategoryId] = useState('all');
  // what is typed in the product search: only the box itself reads its keys (Enter, Escape)
  const [query, setQuery] = useState('');
  // The picker follows the live catalog by id, so modifiers that finish loading (or change) after
  // the tap still show; a captured snapshot would silently drop them.
  const [pickerId, setPickerId] = useState<string | null>(null);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [reason, setReason] = useState<ReasonRequest | null>(null);
  const [showOpen, setShowOpen] = useState(false);
  const [editDetails, setEditDetails] = useState<HeaderState | null>(null);
  const [checkout, setCheckout] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [slip, setSlip] = useState<Receipt | null>(null);
  // ADR-029: after money went back by GCash or a bank transfer, its reference (and a void's answer)
  const [giveBack, setGiveBack] = useState<{ orderId: string; reversalIds: string[] } | null>(null);
  const [showTables, setShowTables] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // ADR-031: write-offs are recorded on the tablet, in a shift
  const [writeOffKind, setWriteOffKind] = useState<WriteOffKind | null>(null);
  const [reinstating, setReinstating] = useState<WriteOffRow | null>(null);
  // ADR-032: a served item handed back, and a payment reference used before
  const [returning, setReturning] = useState<OrderItem | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateRequest | null>(null);

  const currency = ctx.currency;
  const order = activeOrderId ? (detail.data ?? null) : null;
  const canWriteOff = isDevice && s.can('order.write_off');
  const writeOffs = useOrderWriteOffs(order?.order.id ?? null);
  const canReturn = isDevice && s.can('order.return');
  const returns = useOrderReturns(
    order?.order.id ?? null,
    (order?.items ?? []).map((i) => i.id),
  );
  const pickerEmployees = usePickerEmployees(canWriteOff || canReturn);
  const employeeName = (id: string | null) =>
    (pickerEmployees.data ?? []).find((e) => e.id === id)?.display_name ?? 'someone';
  const occupied = new Set<string>((sessions.data ?? []).map((x) => x.dining_table_id));
  const tableName = (id: string | null) =>
    (tables.data ?? []).find((t) => t.id === id)?.name ?? (id ? 'table' : null);
  const stationName = (id: string) =>
    (stations.data ?? []).find((x) => x.id === id)?.name ?? 'station';
  const offlineStations = (health.data ?? []).filter((h) => h.terminals > 0 && !h.online);

  /* ── cart mode ── */
  const addLine = async (line: UiLine) => {
    setPickerId(null);
    if (order) {
      setBusy('add');
      await run('order.add_lines', {
        order_id: order.order.id,
        version: order.order.version,
        lines: [toCartLine(line)],
      });
      setBusy(null);
      return;
    }
    setCart((cur) => {
      const i = cur.findIndex((l) => sameChoice(l, line));
      if (i < 0) return [...cur, line];
      return cur.map((l, j) => (j === i ? { ...l, qty: l.qty + line.qty } : l));
    });
  };
  const tap = (p: PosProduct) => {
    if (needsPicker(p)) {
      setPickerId(p.product.id);
      return;
    }
    const v = p.variants.find((x) => !p.soldOutVariantIds.has(x.id));
    if (!v) return;
    void addLine({
      id: uuidv7(),
      product_id: p.product.id,
      product_variant_id: v.id,
      name: p.product.name,
      variant_name: null,
      unit_price_minor: v.price_minor,
      qty: 1,
      notes: '',
      modifiers: [],
    });
  };
  const setCartQty = (id: string, qty: number) =>
    setCart((cur) =>
      qty <= 0 ? cur.filter((l) => l.id !== id) : cur.map((l) => (l.id === id ? { ...l, qty } : l)),
    );

  const placeOrder = async () => {
    if (cart.length === 0) return;
    if (ctx.principal === 'human' && !ctx.terminal) {
      toast('Select a terminal before placing orders', 'danger');
      return;
    }
    const h = headerPayload(header, ctx.timeZone);
    if (!h.ok) {
      toast(h.error, 'danger');
      return;
    }
    const rec = paper ? paperRecoveryPayload(paper, new Date(), ctx.timeZone) : null;
    if (rec && !rec.ok) {
      toast(rec.error, 'danger');
      return;
    }
    setBusy('place');
    const r = await run('order.place', {
      id: uuidv7(),
      ...(ctx.principal === 'human' && ctx.terminal ? { terminal_id: ctx.terminal.id } : {}),
      ...h.value,
      lines: cart.map(toCartLine),
      ...(rec ? { recovery: rec.value } : {}),
    });
    setBusy(null);
    if (!r.ok) return;
    setCart([]);
    setHeader(EMPTY_HEADER);
    setPaper(null);
    setActiveOrderId(String(r.result.order_id));
    toast(
      `Order ${String(r.result.order_number)} ${rec ? 'entered from paper' : 'placed'}`,
      'success',
    );
  };

  /* ── order mode ── */
  /** ADR-023: a refused command asks for an approval of exactly what it was about to send. */
  const approvalFor = (
    kind: ApprovalAction,
    payload: Record<string, unknown>,
    permission: string,
    e: AppError,
    retry: (a?: Approval) => Promise<void>,
  ) => {
    // only a refusal that names this approval opens the sheet (not "another cashier's shift")
    if (e.code !== 'forbidden' || ctx.principal !== 'device' || hintOf(e).requires !== permission)
      return false;
    setApproval(approvalRequest(kind, payload, approvalCopy(permission, e), retry));
    return true;
  };

  const sendOrder = async (a?: Approval) => {
    if (!order) return;
    setBusy('send');
    const payload = { order_id: order.order.id, version: order.order.version };
    const r = await run(
      'order.send',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          hintOf(e).requires === 'order.send_unpaid' &&
          approvalFor('order.send', payload, 'order.send_unpaid', e, (ap) => sendOrder(ap)),
      },
    );
    setBusy(null);
    if (r.ok) {
      const n = Array.isArray(r.result.tickets) ? r.result.tickets.length : 0;
      toast(
        n > 0 ? `Sent: ${n} ticket${n === 1 ? '' : 's'}` : 'Nothing to prepare; order marked ready',
        'success',
      );
    }
  };

  const capture = async (tenders: TenderInput[], partial: boolean) => {
    if (!order) return;
    setBusy('pay');
    // ADR-024: a payment carries the shift the tablet shows. One taken while that shift takes money
    // but that cannot be sent at once is marked queued, so if the shift stops taking money meanwhile
    // it still lands on it (a late entry). Never while the drawer is counted or the shift's date
    // has passed: then it is refused, not filed late.
    const shown = isDevice ? (shiftState.data?.shift ?? null) : null;
    const shiftId = shown?.id ?? null;
    const box = outbox.status();
    const queued =
      !!shown &&
      shown.status === 'open' &&
      !shown.stale &&
      ((typeof navigator !== 'undefined' && !navigator.onLine) ||
        box.pending - box.parked - box.held > 0);
    const r = await run(
      'payment.capture',
      {
        order_id: order.order.id,
        version: order.order.version,
        tenders,
        partial,
        ...(shiftId ? { shift_id: shiftId } : {}),
        ...(queued ? { queued: true } : {}),
        ...moneyAt,
      },
      {
        // D5 (ADR-032): a reference used before is asked about, never taken silently
        handle: (e) => {
          const hint = duplicateOf(e);
          if (!hint) return false;
          setDuplicate({
            hint,
            onConfirm: (why) => capture(withDuplicateReason(tenders, hint, why), partial),
          });
          return true;
        },
      },
    );
    setBusy(null);
    if (!r.ok) return;
    setCheckout(false);
    dropRecordedFailures(r.result);
    const change = Number(r.result.change_minor ?? 0);
    const status = String(r.result.payment_status);
    if (status === 'paid' || status === 'written_off')
      toast(
        change > 0
          ? `Paid. Change ${formatMinor(change, currency)}`
          : r.result.completed
            ? 'Paid and completed'
            : 'Paid',
        'success',
      );
    else
      toast(
        `Deposit recorded; ${formatMinor(Number(r.result.remaining_minor ?? 0), currency)} remaining`,
        'success',
      );
    const auto = r.result.auto_send as { attempted?: boolean; ok?: boolean; error?: string } | null;
    if (auto && auto.attempted && auto.ok === false)
      toast(
        `Paid, but not sent: ${auto.error ?? 'production could not accept the order'}. Send it once fixed.`,
        'danger',
      );
    else if (auto && auto.ok) toast('Sent to production automatically', 'success');
  };

  const voidPayment = (p: Payment, reversibleMinor: number) => {
    if (!order) return;
    const opened = { id: order.order.id, version: order.order.version };
    setReason({
      // a void reverses what is left of the tender: its amount minus what was refunded from it
      title: `Void ${p.method_name_snapshot} ${formatMinor(reversibleMinor, currency)}`,
      label: 'Reason',
      required: true,
      confirmLabel: 'Void payment',
      danger: true,
      detail: `Reverses ${formatMinor(reversibleMinor, currency)} of this ${p.method_name_snapshot} payment; the order owes it again.`,
      onConfirm: (why) => doVoidPayment(opened, p, why),
    });
  };
  const doVoidPayment = async (
    opened: { id: string; version: number },
    p: Payment,
    why: string,
    a?: Approval,
    cashMovesNow = false,
  ) => {
    const payload = {
      order_id: opened.id,
      // the order as the screen last received it, read now: not as it was when the sheet opened
      version: latestOrderVersion(qc, opened.id, opened.version),
      payment_id: p.id,
      reason: why,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'payment.void',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => doVoidPayment(opened, p, why, a, true)) ||
          approvalFor('payment.void', payload, 'refund.approve', e, (ap) =>
            doVoidPayment(opened, p, why, ap, cashMovesNow),
          ),
      },
    );
    if (r.ok) {
      const reversedMinor = Number(r.result.reversed_minor ?? 0);
      const reversed = formatMinor(reversedMinor, currency);
      const note = allowanceNote(r.result, reversedMinor, currency);
      toast(
        r.result.reopened
          ? `Payment voided; ${reversed} reversed; order reopened${note}`
          : `Payment voided; ${reversed} reversed${note}`,
      );
      setGiveBack({ orderId: opened.id, reversalIds: reversalIdsOf(r.result) });
    }
  };

  const refundOrder = async (input: RefundInput, a?: Approval, cashMovesNow = false) => {
    if (!order) return;
    setBusy('refund');
    const payload = {
      order_id: order.order.id,
      version: order.order.version,
      ...input,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'order.refund',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => refundOrder(input, a, true)) ||
          approvalFor('order.refund', payload, 'refund.approve', e, (ap) =>
            refundOrder(input, ap, cashMovesNow),
          ),
      },
    );
    setBusy(null);
    if (r.ok) {
      setRefunding(false);
      toast(
        `Refunded ${formatMinor(input.amount_minor, currency)}${allowanceNote(r.result, input.amount_minor, currency)}`,
        'success',
      );
      setGiveBack({ orderId: order.order.id, reversalIds: reversalIdsOf(r.result) });
    }
  };

  /* ── write-offs (ADR-031): beyond the allowance, and a comp, the owner approves (writeoff.approve) ── */
  const doWriteOff = async (input: WriteOffInput, a?: Approval) => {
    if (!order) return;
    setBusy('write-off');
    const payload = { order_id: order.order.id, version: order.order.version, ...input };
    const r = await run(
      'order.write_off',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          approvalFor('order.write_off', payload, 'writeoff.approve', e, (ap) =>
            doWriteOff(input, ap),
          ),
      },
    );
    setBusy(null);
    if (r.ok) {
      setWriteOffKind(null);
      const amount = formatMinor(Number(r.result.amount_minor ?? 0), currency);
      toast(
        `${WRITE_OFF_LABEL[input.kind]}: ${amount} written off${r.result.completed ? '; order completed' : ''}`,
        'success',
      );
    }
  };
  const doReinstate = async (row: WriteOffRow, input: { reason: string; collectCash: boolean }) => {
    if (!order) return;
    const cash = (methods.data ?? []).find((m) => m.kind === 'cash' && m.enabled && !m.archived_at);
    setBusy('reinstate');
    const r = await run('order.write_off_reinstate', {
      write_off_id: row.id,
      version: order.order.version,
      reason: input.reason,
      ...(input.collectCash && cash
        ? { tenders: [{ payment_method_id: cash.id, amount_minor: row.amount_minor }] }
        : {}),
    });
    setBusy(null);
    if (r.ok) {
      setReinstating(null);
      toast(
        r.result.capture
          ? `${formatMinor(row.amount_minor, currency)} collected`
          : `${formatMinor(row.amount_minor, currency)} owed again`,
        'success',
      );
    }
  };

  /* ── returns (ADR-032): beyond the walk-outs' allowance the owner approves (writeoff.approve) ── */
  const doReturn = async (
    item: OrderItem,
    input: ReturnInput,
    a?: Approval,
    cashMovesNow = false,
  ) => {
    if (!order) return;
    setBusy('return');
    const payload = {
      order_id: order.order.id,
      version: order.order.version,
      order_item_id: item.id,
      ...input,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'order.return_line',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => doReturn(item, input, a, true)) ||
          approvalFor('order.return_line', payload, 'writeoff.approve', e, (ap) =>
            doReturn(item, input, ap, cashMovesNow),
          ),
      },
    );
    setBusy(null);
    if (r.ok) {
      setReturning(null);
      const amount = formatMinor(Number(r.result.amount_minor ?? 0), currency);
      const back = Number(r.result.refund_minor ?? 0);
      toast(
        `Returned: ${amount} off the order${back > 0 ? `; ${formatMinor(back, currency)} given back` : ''}${r.result.completed ? '; order completed' : ''}`,
        'success',
      );
      setGiveBack({ orderId: order.order.id, reversalIds: reversalIdsOf(r.result) });
    }
  };

  const updateLine = async (item: OrderItem, qty: number) => {
    if (!order) return;
    setBusy(item.id);
    if (qty <= 0)
      await run('order.remove_line', {
        order_id: order.order.id,
        version: order.order.version,
        order_item_id: item.id,
      });
    else
      await run('order.update_line', {
        order_id: order.order.id,
        version: order.order.version,
        order_item_id: item.id,
        qty,
      });
    setBusy(null);
  };

  const voidLine = (item: OrderItem) =>
    setReason({
      title: `Void ${item.name_snapshot}`,
      label: 'Reason',
      required: true,
      confirmLabel: 'Void line',
      danger: true,
      detail: `Takes ${item.qty} × ${item.name_snapshot} (${formatMinor(item.line_total_minor, currency)}) off the order. The line stays on it, struck through, with your reason.`,
      onConfirm: (why) => doVoidLine(item, why),
    });
  const doVoidLine = async (item: OrderItem, why: string, a?: Approval) => {
    if (!order) return;
    const payload = {
      order_id: order.order.id,
      version: order.order.version,
      order_item_id: item.id,
      reason: why,
    };
    await run(
      'order.void_line',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          approvalFor('order.void_line', payload, 'order.void.approve', e, (ap) =>
            doVoidLine(item, why, ap),
          ),
      },
    );
  };

  const cancelOrder = () =>
    setReason({
      title: 'Cancel order',
      label: 'Reason',
      required: false,
      confirmLabel: 'Cancel order',
      danger: true,
      ...(order
        ? {
            detail: `Cancels order ${order.order.order_number} (${formatMinor(order.order.total_minor, currency)}): nothing was sent or paid yet.`,
          }
        : {}),
      onConfirm: async (why) => {
        if (!order) return;
        const r = await run('order.cancel', {
          order_id: order.order.id,
          version: order.order.version,
          ...(why ? { reason: why } : {}),
        });
        if (r.ok) {
          toast(`Order ${order.order.order_number} cancelled`);
          setActiveOrderId(null);
        }
      },
    });

  const voidOrder = () => {
    const back = order ? refundableMinor(money(order.order)) : 0;
    setReason({
      title: 'Void order',
      label: 'Reason',
      required: true,
      confirmLabel: 'Void order',
      danger: true,
      ...(order
        ? {
            detail: `Voids order ${order.order.order_number} (${formatMinor(order.order.total_minor, currency)}).${back > 0 ? ` ${formatMinor(back, currency)} paid on it is reversed.` : ''}`,
          }
        : {}),
      onConfirm: (why) => doVoidOrder(why),
    });
  };
  const doVoidOrder = async (why: string, a?: Approval, cashMovesNow = false) => {
    if (!order) return;
    const payload = {
      order_id: order.order.id,
      version: order.order.version,
      reason: why,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'order.void',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => doVoidOrder(why, a, true)) ||
          approvalFor('order.void', payload, voidApprovalPermission(e), e, (ap) =>
            doVoidOrder(why, ap, cashMovesNow),
          ),
      },
    );
    if (r.ok) {
      const reversed = Number(r.result.reversed_minor ?? 0);
      toast(
        reversed > 0
          ? `Order ${order.order.order_number} voided; ${formatMinor(reversed, currency)} reversed`
          : `Order ${order.order.order_number} voided`,
      );
      if (reversed > 0)
        setGiveBack({ orderId: order.order.id, reversalIds: reversalIdsOf(r.result) });
      setActiveOrderId(null);
    }
  };

  const saveDetails = async () => {
    if (!order || !editDetails) return;
    const h = headerPayload(editDetails, ctx.timeZone);
    if (!h.ok) {
      toast(h.error, 'danger');
      return;
    }
    setBusy('details');
    const r = await run('order.update', {
      order_id: order.order.id,
      version: order.order.version,
      ...h.value,
    });
    setBusy(null);
    if (r.ok) setEditDetails(null);
  };

  /* ── serving from the counter (owner decision 6): takeout pickups and dine-in handovers ── */
  const canServe = s.can('serve.operate') && (ctx.principal === 'device' || !!ctx.terminal);
  const servePayload = () =>
    ctx.principal === 'human' && ctx.terminal ? { terminal_id: ctx.terminal.id } : {};
  const afterServe = (r: { ok: boolean; result?: Record<string, unknown> }) => {
    if (!r.ok) return;
    const o = (r.result?.order ?? {}) as { status?: string; fulfilment_status?: string };
    toast(
      o.status === 'completed'
        ? 'Served; order completed'
        : o.fulfilment_status === 'served'
          ? 'Served; order to settle'
          : 'Served',
      'success',
    );
  };
  const serveTicket = async (t: Ticket) => {
    setBusy(`serve:${t.id}`);
    const r = await serve.run(
      'ticket.serve',
      { ticket_id: t.id, ...servePayload() },
      t.id,
      'Serve',
    );
    setBusy(null);
    afterServe(r);
  };
  const serveAll = async () => {
    if (!order) return;
    setBusy('serve:all');
    const r = await serve.run(
      'order.serve',
      { order_id: order.order.id, ...servePayload() },
      order.order.id,
      'Serve all',
    );
    setBusy(null);
    afterServe(r);
  };
  const unconfirmedServes = Object.values(serve.pending).filter((p) => p.state === 'unconfirmed');
  /** ADR-022: the cashier corrects a serve directly after the window, with a reason and no PIN. */
  const correctServe = serveCorrection({
    run: serve.run,
    toast,
    setReason,
    setApproval,
    terminalPayload: servePayload(),
    isDevice: ctx.principal === 'device',
    canCorrect: s.can('serve.correct'),
    undoWindow,
  });
  /** Critical refinement 2: a new purchase after completion is a new order on the same table. */
  const newOrderHere = (tableId: string, covers: number | null) => {
    setHeader({
      ...EMPTY_HEADER,
      type: 'dine_in',
      dining_table_id: tableId,
      covers: covers ? String(covers) : '',
    });
    setActiveOrderId(null);
    setShowTables(false);
  };

  // The menu as the cashier browses it: the categories with something on them, in their own order, each
  // with its products in theirs; All is all of them in that order, then the products of no category.
  const browse = useMemo(() => {
    const known = new Set(catalog.categories.map((c) => c.id));
    const byCategory = new Map<string, PosProduct[]>();
    const loose: PosProduct[] = [];
    for (const p of catalog.products) {
      const c = p.product.category_id;
      if (!c || !known.has(c)) loose.push(p);
      else if (byCategory.has(c)) byCategory.get(c)?.push(p);
      else byCategory.set(c, [p]);
    }
    const categories = catalog.categories.filter((c) => byCategory.has(c.id));
    return {
      categories,
      byCategory,
      all: [...categories.flatMap((c) => byCategory.get(c.id) ?? []), ...loose],
    };
  }, [catalog.categories, catalog.products]);
  const words = searchWords(query);
  const searching = words.length > 0;
  // a category left with nothing on it while it was chosen shows All
  const shownCategory =
    categoryId !== 'all' && browse.byCategory.has(categoryId) ? categoryId : 'all';
  const visibleProducts = searching
    ? browse.all.filter((p) => nameMatches(p.product.name, words))
    : shownCategory === 'all'
      ? browse.all
      : (browse.byCategory.get(shownCategory) ?? []);
  const chooseCategory = (id: string) => {
    setCategoryId(id);
    setQuery('');
  };
  // another category or search starts at its first products (the product area keeps its own scroll)
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = 0;
  }, [shownCategory, query]);
  const categoryColor = new Map(catalog.categories.map((c) => [c.id, c.color] as const));
  // an order picked from the list (or just placed) is still loading: a tap must not land in the cart
  const orderLoading = !!activeOrderId && !order;
  /** When a product's tap does nothing: the same rule for its tile and for Enter in the search. */
  const tapPaused = (p: PosProduct) =>
    p.soldOut ||
    !!busy ||
    // variants, modifiers and their rules must all be loaded before a tap counts
    catalog.loading ||
    orderLoading ||
    // ADR-037: a line on an order needs the server now (a cart line does not)
    (!!order && (order.order.status !== 'open' || !online));
  // how many of each product the cart (or the open order) holds, shown on its tile; none while an order
  // picked from the list is still loading (the cart is out of sight then)
  const inOrder = new Map<string, number>();
  if (order) {
    const productOf = new Map(
      catalog.products.flatMap((p) => p.variants.map((v) => [v.id, p.product.id] as const)),
    );
    for (const i of order.items) {
      const variant = i.status === 'active' ? i.product_variant_id : null;
      const id = variant ? productOf.get(variant) : undefined;
      if (id) inOrder.set(id, (inOrder.get(id) ?? 0) + i.qty);
    }
  } else if (!activeOrderId)
    for (const l of cart) inOrder.set(l.product_id, (inOrder.get(l.product_id) ?? 0) + l.qty);

  // the page's title, its terminal and Tables show in the app's top bar (Shell keeps a place for them there), so
  // the POS spends no row of its own on them; the same header at every screen size
  const pageHeader = (
    <PageHeader
      eyebrow="Cashier"
      title="POS"
      subtitle={
        ctx.terminal
          ? `Terminal ${ctx.terminal.code} · ${ctx.terminal.name}`
          : ctx.principal === 'human'
            ? 'Choose the terminal these orders belong to.'
            : undefined
      }
      actions={
        <>
          <TerminalPicker ctx={ctx} />
          {/* a café without tables set up has nothing here (both order types stay on the cart) */}
          {tables.data?.length !== 0 && (
            <Button variant="ghost" onClick={() => setShowTables(true)} data-testid="pos-tables">
              Tables ({occupied.size} occupied)
            </Button>
          )}
        </>
      }
    />
  );

  return (
    <div className="d-page d-page--wide pos-page">
      {shellBar ? createPortal(pageHeader, shellBar) : pageHeader}

      {offlineStations.length > 0 && (
        <Notice tone="warning" role="status" testId="station-offline">
          {offlineStations.map((h) => (
            <div key={h.station_id}>{describeOffline(h)}</div>
          ))}
        </Notice>
      )}
      {unconfirmedServes.map((p) => (
        <Notice
          key={p.target}
          tone="warning"
          role="alert"
          testId="serve-unconfirmed"
          action={
            <span className="d-actions">
              <Button
                size="sm"
                variant="primary"
                disabled={!online}
                onClick={() => serve.retry(p.target)}
              >
                Retry
              </Button>
              <Button size="sm" variant="ghost" onClick={() => serve.discard(p.target)}>
                Discard
              </Button>
            </span>
          }
        >
          Action not confirmed — {p.label}
        </Notice>
      ))}

      <div className="pos-desk">
        <ShiftDesk
          state={isDevice ? shiftState.data : undefined}
          currency={currency}
          timeZone={ctx.timeZone}
        >
          <div className="d-pos">
            <section className="d-pos__catalog pos-browse" aria-label="Products">
              <div className="pos-search">
                <Input
                  type="search"
                  className="pos-search__input"
                  placeholder="Search products"
                  aria-label="Search products"
                  autoComplete="off"
                  spellCheck={false}
                  enterKeyHint="go"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // the box's own keys only: Enter adds the one product found, Escape clears
                    if (e.key === 'Escape' && query) {
                      e.preventDefault();
                      setQuery('');
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const only =
                        searching && visibleProducts.length === 1 ? visibleProducts[0] : null;
                      if (only && !tapPaused(only)) {
                        tap(only);
                        setQuery('');
                      }
                    }
                  }}
                  data-testid="pos-search"
                />
              </div>
              <div
                className="pos-cats"
                role="group"
                aria-label="Category"
                data-testid="pos-categories"
              >
                <button
                  type="button"
                  className="pos-chip"
                  aria-pressed={!searching && shownCategory === 'all'}
                  onClick={() => chooseCategory('all')}
                >
                  All
                </button>
                {browse.categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="pos-chip"
                    aria-pressed={!searching && shownCategory === c.id}
                    onClick={() => chooseCategory(c.id)}
                  >
                    {c.color && (
                      <span className="pos-swatch" style={{ background: c.color }} aria-hidden />
                    )}
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="pos-grid" ref={gridRef}>
                {catalog.error && (
                  <ErrorState
                    title="Could not load the catalog"
                    message={catalog.error}
                    onRetry={catalog.retry}
                  />
                )}
                {catalog.loading ? (
                  <Loading label="Loading the catalog…" />
                ) : (
                  visibleProducts.length === 0 &&
                  !catalog.error &&
                  (searching ? (
                    <EmptyState title={`Nothing matches “${query.trim()}”`}>
                      Try fewer letters, or clear the search.
                    </EmptyState>
                  ) : (
                    <EmptyState title="No products here yet">Add them under Catalog.</EmptyState>
                  ))
                )}
                <div className="d-tiles pos-tiles" data-testid="pos-tiles">
                  {visibleProducts.map((p) => {
                    // the colour stripe only where the menu gives the product or its category a colour
                    const color =
                      p.product.color || categoryColor.get(p.product.category_id ?? '') || null;
                    const qty = inOrder.get(p.product.id) ?? 0;
                    return (
                      <button
                        key={p.product.id}
                        type="button"
                        className={`d-tile pos-tile${p.soldOut ? ' pos-tile--out' : ''}`}
                        disabled={tapPaused(p)}
                        onClick={() => tap(p)}
                        aria-label={p.product.name}
                        data-testid="pos-tile"
                      >
                        {color && (
                          <span
                            className="pos-tile__swatch"
                            style={{ background: color }}
                            aria-hidden
                          />
                        )}
                        {qty > 0 && (
                          <span className="pos-tile__qty" data-testid="pos-tile-qty">
                            {qty}
                          </span>
                        )}
                        <span className="d-tile__name pos-tile__name">{p.product.name}</span>
                        {p.soldOut ? (
                          <span className="pos-tile__out">
                            <Badge>Sold out</Badge>
                          </span>
                        ) : (
                          <span className="pos-tile__price">
                            {p.variants.length > 1 && <span className="pos-tile__from">from </span>}
                            {formatMinor(p.fromMinor, currency)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* the order sits a step higher than the products (ADR-042): depth 3 inside a depth-2 screen */}
            <aside
              className="d-pos__cart pos-cart"
              data-depth="3"
              data-testid="cart"
              aria-label="Order"
            >
              {order ? (
                <OrderPanel
                  detail={order}
                  money={orderMoney.data}
                  currency={currency}
                  timeZone={ctx.timeZone}
                  tableName={tableName}
                  stationName={stationName}
                  busy={busy}
                  canVoidDirect={s.can('order.void')}
                  canPay={s.can('payment.capture')}
                  canVoidPayment={s.can('payment.void') || isDevice}
                  canRefund={s.can('refund.perform') || isDevice}
                  canServe={canServe}
                  canCorrectServe={s.can('serve.correct')}
                  undoWindow={undoWindow}
                  onCorrectServe={correctServe}
                  isDevice={isDevice}
                  onServeTicket={(t) => void serveTicket(t)}
                  onServeAll={() => void serveAll()}
                  onNewOrderHere={() =>
                    newOrderHere(order.order.dining_table_id ?? '', order.order.covers)
                  }
                  onSend={() => void sendOrder()}
                  onPay={() => setCheckout(true)}
                  onRefund={() => setRefunding(true)}
                  onVoidPayment={voidPayment}
                  onSlip={setSlip}
                  onQty={(item, qty) => void updateLine(item, qty)}
                  onVoidLine={voidLine}
                  onCancel={cancelOrder}
                  onVoid={voidOrder}
                  onEdit={() => setEditDetails(headerFromOrder(order.order, ctx.timeZone))}
                  returns={canReturn ? (returns.data ?? null) : null}
                  returnedOf={(i) => returns.data?.returned[i.id] ?? 0}
                  onReturnLine={setReturning}
                  moneyExtra={
                    <>
                      {orderMoney.error && !orderMoney.data && (
                        <ErrorState
                          title="Could not load the payments"
                          message={errorText(orderMoney.error)}
                          onRetry={() => void orderMoney.refetch()}
                        />
                      )}
                      <ReturnsPanel
                        rows={returns.data?.rows ?? []}
                        items={order.items}
                        currency={currency}
                        nameOf={employeeName}
                      />
                      <WriteOffsPanel
                        order={order.order}
                        rows={writeOffs.data ?? []}
                        currency={currency}
                        canWriteOff={canWriteOff}
                        nameOf={employeeName}
                        onStart={setWriteOffKind}
                        onReinstate={setReinstating}
                      />
                      <GiveBacksPanel orderId={order.order.id} currency={currency} run={run} />
                    </>
                  }
                />
              ) : orderLoading ? (
                detail.error ? (
                  <ErrorState
                    title="Could not load this order"
                    message={errorText(detail.error)}
                    onRetry={() => void detail.refetch()}
                  />
                ) : !online ? (
                  // the read waits for the network (ADR-037): say so rather than spin
                  <EmptyState title="Waiting for the connection" testId="order-waiting">
                    This order opens when the connection is back.
                  </EmptyState>
                ) : (
                  <Loading rows={3} label="Loading the order…" />
                )
              ) : (
                <>
                  <Surface pad="sm" flat className="pos-head">
                    <OrderHeaderFields
                      value={header}
                      onChange={setHeader}
                      tables={tables.data ?? []}
                      noTables={tables.data?.length === 0}
                      occupied={occupied}
                      timeZone={ctx.timeZone}
                      compact
                    />
                    <PaperOrderFields value={paper} onChange={setPaper} />
                  </Surface>
                  <Surface pad="none" flat className="pos-body">
                    <div className="pos-body__scroll">
                      <div className="pos-lines" data-testid="cart-lines">
                        {cart.length === 0 && <EmptyState title="Tap products to add them" />}
                        {cart.map((l) => {
                          const what = describeLine(l);
                          return (
                            <div className="pos-line" key={l.id} data-testid="cart-line">
                              <div className="pos-line__main">
                                <div className="pos-line__title">{l.name}</div>
                                {what && <div className="pos-line__detail">{what}</div>}
                                {l.notes.trim() && (
                                  <div className="pos-line__note">“{l.notes.trim()}”</div>
                                )}
                              </div>
                              <div className="pos-line__side">
                                <span className="pos-line__total">
                                  {formatMinor(uiLineTotal(l), currency)}
                                </span>
                                <QtyStepper
                                  value={l.qty}
                                  min={0}
                                  onChange={(n) => setCartQty(l.id, n)}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="pos-body__foot">
                      <div className="pos-total">
                        <span className="pos-total__label">Total</span>
                        <strong className="pos-total__value" data-testid="cart-total">
                          {formatMinor(
                            cart.reduce((a, l) => a + uiLineTotal(l), 0),
                            currency,
                          )}
                        </strong>
                      </div>
                      <Button
                        variant="primary"
                        size="lg"
                        block
                        busy={busy === 'place'}
                        disabled={!online || cart.length === 0}
                        onClick={() => void placeOrder()}
                        data-testid="place-order"
                      >
                        {paper ? 'Enter paper order' : 'Place order'}
                      </Button>
                    </div>
                  </Surface>
                </>
              )}
              {/* the cashier's other actions, under the order and inside the shift: no open shift, none of them */}
              <div className="pos-actions">
                <Button variant="ghost" onClick={() => setShowOpen(true)} data-testid="open-orders">
                  Open orders ({openOrders.data?.length ?? 0})
                </Button>
                {activeOrderId ? (
                  <Button onClick={() => setActiveOrderId(null)} data-testid="new-order">
                    New order
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    disabled={cart.length === 0 || busy === 'place'}
                    onClick={() => {
                      setCart([]);
                      setHeader(EMPTY_HEADER);
                      setPaper(null);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </aside>
          </div>
        </ShiftDesk>
      </div>

      <LineSheet
        product={
          pickerId ? (catalog.products.find((x) => x.product.id === pickerId) ?? null) : null
        }
        currency={currency}
        needsServer={!!order}
        onAdd={(l) => void addLine(l)}
        onClose={() => setPickerId(null)}
      />
      <ReasonSheet request={reason} onClose={() => setReason(null)} />
      <CheckoutSheet
        order={checkout && order ? order.order : null}
        methods={methods.data ?? []}
        currency={currency}
        busy={busy === 'pay'}
        onClose={() => setCheckout(false)}
        onCapture={capture}
      />
      <RefundSheet
        order={refunding && order ? order.order : null}
        items={order?.items ?? []}
        methods={methods.data ?? []}
        currency={currency}
        busy={busy === 'refund'}
        onClose={() => setRefunding(false)}
        onRefund={(input) => refundOrder(input)}
      />
      <SlipSheet receipt={slip} currency={currency} onClose={() => setSlip(null)} />
      <WriteOffSheet
        kind={writeOffKind}
        order={order?.order ?? null}
        currency={currency}
        employees={pickerEmployees.data ?? []}
        defaultEmployeeId={s.me?.terminal_session?.employee_id ?? null}
        busy={busy === 'write-off'}
        onClose={() => setWriteOffKind(null)}
        onSubmit={(input) => doWriteOff(input)}
      />
      <ReturnSheet
        item={returning}
        order={order?.order ?? null}
        returnable={
          returning && order && returns.data ? returnableOf(returning, order, returns.data) : 0
        }
        returnedBefore={returning ? (returns.data?.returned[returning.id] ?? 0) : 0}
        methods={methods.data ?? []}
        currency={currency}
        busy={busy === 'return'}
        onClose={() => setReturning(null)}
        onSubmit={(input) => (returning ? doReturn(returning, input) : Promise.resolve())}
      />
      <ReinstateSheet
        row={reinstating}
        currency={currency}
        // in one step only when the write-off is all that will be owed, in cash
        canCollectCash={
          s.can('payment.capture') &&
          !!order &&
          remainingMinor(money(order.order)) === 0 &&
          (methods.data ?? []).some((m) => m.kind === 'cash' && m.enabled && !m.archived_at)
        }
        busy={busy === 'reinstate'}
        onClose={() => setReinstating(null)}
        onSubmit={(input) => (reinstating ? doReinstate(reinstating, input) : Promise.resolve())}
      />
      {giveBack && giveBack.reversalIds.length > 0 && (
        <GiveBackSheet
          orderId={giveBack.orderId}
          reversalIds={giveBack.reversalIds}
          currency={currency}
          run={run}
          onClose={() => setGiveBack(null)}
        />
      )}

      <Sheet open={showTables} onClose={() => setShowTables(false)} title="Tables">
        {tables.isLoading || sessions.isLoading ? (
          <Loading rows={3} label="Loading the tables…" />
        ) : (tables.error && !tables.data) || (sessions.error && !sessions.data) ? (
          <ErrorState
            title="Could not load the tables"
            message={errorText(tables.error ?? sessions.error)}
            onRetry={() => {
              void tables.refetch();
              void sessions.refetch();
            }}
          />
        ) : (
          <TableOccupancyList
            tables={tables.data ?? []}
            sessions={sessions.data ?? []}
            orders={openOrders.data ?? []}
            currency={currency}
            timeZone={ctx.timeZone}
            canClear={s.can('table.clear')}
            busy={tableClear.busy}
            onClear={(t) => void tableClear.clear(t)}
            onNewOrder={(t) => newOrderHere(t.id, null)}
          />
        )}
      </Sheet>
      <ForceClearSheet
        request={tableClear.force}
        busy={!!tableClear.busy}
        onConfirm={(why) => void tableClear.forceClear(why)}
        onClose={tableClear.cancel}
      />

      <Sheet open={showOpen} onClose={() => setShowOpen(false)} title="Open orders">
        {openOrders.isLoading ? (
          <Loading rows={3} label="Loading the open orders…" />
        ) : openOrders.error && !openOrders.data ? (
          <ErrorState
            title="Could not load the open orders"
            message={errorText(openOrders.error)}
            onRetry={() => void openOrders.refetch()}
          />
        ) : (
          <div className="d-list" data-testid="open-orders-list">
            {(openOrders.data ?? []).length === 0 && <EmptyState title="No open orders" />}
            {(openOrders.data ?? []).map((o) => (
              <button
                key={o.id}
                type="button"
                className="d-row d-row--button"
                onClick={() => {
                  setActiveOrderId(o.id);
                  setShowOpen(false);
                }}
                data-testid={`open-order-${o.order_number}`}
              >
                <div className="d-row__main">
                  <div className="d-row__title pos-order-row__title">
                    <span className="d-mono">{o.order_number}</span>
                    <Badge tone="accent">{ORDER_TYPE_LABEL[o.type]}</Badge>
                    {o.dining_table_id && (
                      <span className="pos-where__place">{tableName(o.dining_table_id)}</span>
                    )}
                    {o.scheduled_for && (
                      <span className="pos-where__place">
                        Ready at {formatInTimeZone(o.scheduled_for, ctx.timeZone)}
                      </span>
                    )}
                    {o.customer_name && <span className="pos-where__who">{o.customer_name}</span>}
                  </div>
                  <div className="d-row__meta">
                    {formatInTimeZone(o.placed_at, ctx.timeZone)} ·{' '}
                    <span className="d-mono">{formatMinor(o.total_minor, currency)}</span>
                  </div>
                </div>
                <span className="pos-order-row__status">
                  <Badge tone={PAYMENT_STATUS_TONE[o.payment_status]}>
                    {o.payment_status.replace('_', ' ')}
                  </Badge>
                  {isToSettle(o) && <Badge tone="warning">TO SETTLE</Badge>}
                  <Badge tone={fulfilmentTone(o.fulfilment_status)}>
                    {FULFILMENT_LABEL[o.fulfilment_status]}
                  </Badge>
                </span>
              </button>
            ))}
          </div>
        )}
      </Sheet>

      <Sheet open={!!editDetails} onClose={() => setEditDetails(null)} title="Order details">
        {editDetails && (
          <div className="d-stack">
            <OrderHeaderFields
              value={editDetails}
              onChange={setEditDetails}
              tables={tables.data ?? []}
              noTables={tables.data?.length === 0}
              occupied={occupied}
              timeZone={ctx.timeZone}
            />
            <div className="d-sheet__actions">
              <Button variant="ghost" onClick={() => setEditDetails(null)}>
                Back
              </Button>
              <Button
                variant="primary"
                busy={busy === 'details'}
                disabled={!online}
                onClick={() => void saveDetails()}
                data-testid="details-save"
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Sheet>
      {cashNow.sheet}
      {/* above the checkout sheet, which stays open behind it */}
      <DuplicateReferenceSheet
        request={duplicate}
        currency={currency}
        timeZone={ctx.timeZone}
        onClose={() => setDuplicate(null)}
      />
      {/* last, so it stacks above a sheet that stays open behind it (the refund sheet) */}
      <ApprovalSheet request={approval} onClose={() => setApproval(null)} />
    </div>
  );
}

function OrderPanel({
  detail,
  money: orderMoney,
  currency,
  timeZone,
  tableName,
  stationName,
  busy,
  canVoidDirect,
  canPay,
  canVoidPayment,
  canRefund,
  canServe,
  canCorrectServe,
  undoWindow,
  onCorrectServe,
  isDevice,
  onServeTicket,
  onServeAll,
  onNewOrderHere,
  onSend,
  onPay,
  onRefund,
  onVoidPayment,
  onSlip,
  onQty,
  onVoidLine,
  onCancel,
  onVoid,
  onEdit,
  returns,
  returnedOf,
  onReturnLine,
  moneyExtra,
}: {
  detail: OrderDetail;
  money: OrderMoney | undefined;
  currency: string;
  timeZone: string;
  tableName: (id: string | null) => string | null;
  stationName: (id: string) => string;
  busy: string | null;
  canVoidDirect: boolean;
  canPay: boolean;
  canVoidPayment: boolean;
  canRefund: boolean;
  canServe: boolean;
  canCorrectServe: boolean;
  undoWindow: number;
  onCorrectServe: CorrectServe;
  isDevice: boolean;
  onServeTicket: (t: Ticket) => void;
  onServeAll: () => void;
  onNewOrderHere: () => void;
  onSend: () => void;
  onPay: () => void;
  onRefund: () => void;
  onVoidPayment: (p: Payment, reversibleMinor: number) => void;
  onSlip: (r: Receipt) => void;
  onQty: (item: OrderItem, qty: number) => void;
  onVoidLine: (item: OrderItem) => void;
  onCancel: () => void;
  onVoid: () => void;
  onEdit: () => void;
  /** ADR-032: what of each served line can be returned; null when this employee cannot return. */
  returns: OrderReturns | null;
  returnedOf: (item: OrderItem) => number;
  onReturnLine: (item: OrderItem) => void;
  /** Under the money panel: the order's money given back (ADR-029). */
  moneyExtra?: ReactNode;
}) {
  // ADR-037: while the server cannot be reached nothing here records anything
  const online = useOnline();
  const o = detail.order;
  const open = o.status === 'open';
  const active = detail.items.filter((i) => i.status === 'active');
  const unsent = active.filter((i) => i.sent_at === null);
  const modsOf = (id: string) => detail.modifiers.filter((m) => m.order_item_id === id);
  const canVoid = canVoidDirect || isDevice; // on a device, someone without order.void asks for a PIN
  const remaining = remainingMinor(money(o));
  const refundable = refundableMinor(money(o));
  const paid = isSettled(o.payment_status);
  const offer = serveOffer(detail.tickets);
  // ADR-032: a returned line keeps what it was; an order with a return is not cancelled or voided
  const hasReturns = detail.items.some((i) => returnedOf(i) > 0);
  // ADR-032: items handed back come off the total; the subtotal still counts them
  const returnedMinor = o.subtotal_minor - o.total_minor;
  const showPay = open && remaining > 0 && canPay;
  const showNewHere = o.status === 'completed' && o.type === 'dine_in' && !!o.dining_table_id;
  const showRefund = (open || o.status === 'completed') && refundable > 0 && canRefund;
  const showCancel =
    open && o.sent_at === null && o.paid_minor === 0 && o.written_off_minor === 0 && !hasReturns;
  const showVoid = open && (o.sent_at !== null || o.paid_minor > 0) && canVoid && !hasReturns;
  return (
    <>
      <Surface pad="sm" flat className="pos-head">
        <div className="pos-head__top">
          <div>
            <div className="d-eyebrow">Order</div>
            <div className="d-order-number d-mono" data-testid="order-number">
              {o.order_number}
            </div>
          </div>
          {open && (
            <Button variant="ghost" size="sm" onClick={onEdit} data-testid="edit-details">
              Details
            </Button>
          )}
        </div>
        <div className="pos-where">
          <Badge tone="accent">{ORDER_TYPE_LABEL[o.type]}</Badge>
          {o.dining_table_id && (
            <span className="pos-where__place">{tableName(o.dining_table_id)}</span>
          )}
          {o.scheduled_for && (
            <span className="pos-where__place">
              Ready at {formatInTimeZone(o.scheduled_for, timeZone)}
            </span>
          )}
          {o.customer_name && <span className="pos-where__who">{o.customer_name}</span>}
        </div>
        {o.notes && <p className="pos-head__note">“{o.notes}”</p>}
        {/* the statuses themselves for tests: the badges' text "unpaid" also contains "paid" */}
        <div
          className="pos-status"
          data-testid="order-status"
          data-order-status={o.status}
          data-payment-status={o.payment_status}
        >
          {!open && <Badge tone={ORDER_STATUS_TONE[o.status]}>{o.status}</Badge>}
          <Badge tone={PAYMENT_STATUS_TONE[o.payment_status]}>
            {o.payment_status.replace('_', ' ')}
          </Badge>
          <Badge tone={fulfilmentTone(o.fulfilment_status)}>
            {FULFILMENT_LABEL[o.fulfilment_status]}
          </Badge>
          {isToSettle(o) && <Badge tone="warning">TO SETTLE</Badge>}
        </div>
        {detail.tickets.length > 0 && (
          <div className="pos-tickets" data-testid="order-tickets-pos">
            {detail.tickets.map((t) => (
              <span key={t.id} className="pos-ticket">
                <Badge tone={ticketTone(t.status)}>
                  {stationName(t.production_station_id)}
                  {t.is_addition ? ' +' : ''} · {t.status.replace('_', ' ')}
                </Badge>
                {open && canServe && t.status === 'ready' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    busy={busy === `serve:${t.id}`}
                    disabled={!online || busy === 'serve:all'}
                    onClick={() => onServeTicket(t)}
                    data-testid="pos-serve-ticket"
                    aria-label={`serve ${stationName(t.production_station_id)}`}
                  >
                    Serve
                  </Button>
                )}
                {(open || o.status === 'completed') && canServe && t.status === 'served' && (
                  <CorrectServeButton
                    servedAt={t.served_at}
                    undoWindow={undoWindow}
                    canCorrect={canCorrectServe}
                    disabled={!online || !!busy}
                    label={stationName(t.production_station_id)}
                    testIdPrefix="pos"
                    onPress={(nowMs) =>
                      onCorrectServe(t, stationName(t.production_station_id), nowMs)
                    }
                  />
                )}
              </span>
            ))}
          </div>
        )}
        {open && offer !== 'none' && (
          <div className="pos-ready" data-testid="pos-ready-for">
            <Badge tone="success" dot>
              {readyForLabel(o.type)}
            </Badge>
            {offer === 'serve_all' && canServe && (
              <Button
                size="sm"
                variant="primary"
                busy={busy === 'serve:all'}
                disabled={!online}
                onClick={onServeAll}
                data-testid="pos-serve-all"
              >
                Serve all
              </Button>
            )}
          </div>
        )}
      </Surface>
      <Surface pad="none" flat className="pos-body">
        <div className="pos-body__scroll">
          <div className="pos-lines" data-testid="order-lines">
            {detail.items.map((i) => {
              const returned = returnedOf(i);
              const returnable = returns ? returnableOf(i, detail, returns) : 0;
              const what = [
                i.variant_name_snapshot,
                ...modsOf(i.id).map((m) => m.option_name_snapshot),
              ]
                .filter(Boolean)
                .join(' · ');
              const canStep = open && i.status === 'active' && i.sent_at === null && returned === 0;
              const canVoidLine =
                open && i.status === 'active' && i.sent_at !== null && canVoid && returned === 0;
              return (
                <div
                  className={`pos-line${i.status === 'voided' ? ' pos-line--void' : ''}`}
                  key={i.id}
                  data-testid="order-line"
                >
                  <div className="pos-line__main">
                    <div className="pos-line__title">
                      <span className="pos-line__qty">{i.qty} ×</span> {i.name_snapshot}
                    </div>
                    {what && <div className="pos-line__detail">{what}</div>}
                    {i.notes && <div className="pos-line__note">“{i.notes}”</div>}
                    <div className="pos-line__meta">
                      <LineBadge status={i.status} sentAt={i.sent_at} />
                      {i.production_route_code_snapshot && (
                        <span>{i.production_route_code_snapshot}</span>
                      )}
                      {returned > 0 && <span>{returned} returned</span>}
                    </div>
                  </div>
                  <div className="pos-line__side">
                    <span className="pos-line__total">
                      {formatMinor(i.line_total_minor, currency)}
                    </span>
                    {canStep && (
                      <QtyStepper
                        value={i.qty}
                        min={0}
                        disabled={!online || busy === i.id}
                        onChange={(n) => onQty(i, n)}
                      />
                    )}
                    {(canVoidLine || returnable > 0) && (
                      <span className="pos-line__actions">
                        {canVoidLine && (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={!online}
                            onClick={() => onVoidLine(i)}
                            aria-label={`void ${i.name_snapshot}`}
                          >
                            Void
                          </Button>
                        )}
                        {returnable > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!online}
                            onClick={() => onReturnLine(i)}
                            aria-label={`return ${i.name_snapshot}`}
                            data-testid="return-line"
                          >
                            Return
                          </Button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <MoneyPanel
            money={orderMoney}
            currency={currency}
            timeZone={timeZone}
            canVoid={open || o.status === 'completed' ? canVoidPayment : false}
            onVoid={onVoidPayment}
            onSlip={onSlip}
          />
          {moneyExtra}
          {(showRefund || showCancel || showVoid) && (
            <div className="pos-more">
              {showRefund && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!online}
                  onClick={onRefund}
                  data-testid="refund-order"
                >
                  Refund
                </Button>
              )}
              {showCancel && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={!online}
                  onClick={onCancel}
                  data-testid="cancel-order"
                >
                  Cancel order
                </Button>
              )}
              {showVoid && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={!online}
                  onClick={onVoid}
                  data-testid="void-order"
                >
                  Void order
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="pos-body__foot">
          <div className="pos-sums">
            {returnedMinor > 0 && (
              <>
                <div className="pos-sum">
                  <span>Subtotal</span>
                  <span>{formatMinor(o.subtotal_minor, currency)}</span>
                </div>
                <div className="pos-sum">
                  <span>Returned</span>
                  <span>−{formatMinor(returnedMinor, currency)}</span>
                </div>
              </>
            )}
            <div className="pos-total">
              <span className="pos-total__label">Total</span>
              <strong className="pos-total__value" data-testid="order-total">
                {formatMinor(o.total_minor, currency)}
              </strong>
            </div>
            {(o.paid_minor > 0 || o.refunded_minor > 0 || o.written_off_minor > 0) && (
              <div className="pos-money" data-testid="order-money">
                <span className="d-muted">
                  Paid {formatMinor(o.paid_minor, currency)}
                  {o.refunded_minor > 0
                    ? ` · refunded ${formatMinor(o.refunded_minor, currency)}`
                    : ''}
                  {o.written_off_minor > 0
                    ? ` · written off ${formatMinor(o.written_off_minor, currency)}`
                    : ''}
                </span>
                <span
                  className={`pos-remaining ${remaining > 0 ? 'pos-remaining--due' : 'pos-remaining--settled'}`}
                  data-testid="order-remaining"
                >
                  {remaining > 0 ? `Remaining ${formatMinor(remaining, currency)}` : 'Settled'}
                </span>
              </div>
            )}
          </div>
          {(showPay || open || showNewHere) && (
            <div className="pos-primary">
              {showPay && (
                <Button
                  variant="primary"
                  size="lg"
                  block
                  busy={busy === 'pay'}
                  disabled={!online}
                  onClick={onPay}
                  data-testid="pay-order"
                >
                  Pay {formatMinor(remaining, currency)}
                </Button>
              )}
              {open && (
                <Button
                  variant={paid && unsent.length > 0 ? 'primary' : 'default'}
                  size="lg"
                  block
                  busy={busy === 'send'}
                  disabled={!online || unsent.length === 0}
                  onClick={onSend}
                  data-testid="send-order"
                >
                  {o.sent_at ? `Send ${unsent.length} new` : 'Send order'}
                </Button>
              )}
              {showNewHere && (
                <Button
                  variant="primary"
                  size="lg"
                  block
                  onClick={onNewOrderHere}
                  data-testid="new-order-here"
                >
                  New order for {tableName(o.dining_table_id)}
                </Button>
              )}
            </div>
          )}
        </div>
      </Surface>
    </>
  );
}
